"use client";

import * as React from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";

import {cn} from "./utils";

function TooltipProvider({
                             delayDuration = 0,
                             ...props
                         }: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
    return (
        <TooltipPrimitive.Provider
            data-slot="tooltip-provider"
            delayDuration={delayDuration}
            {...props}
        />
    );
}

type TooltipScaleContextValue = {
    registerTrigger: (node: HTMLElement | null) => void;
    registerContent: (node: HTMLDivElement | null) => void;
    offset: {x: number; y: number};
};

const TooltipScaleContext =
    React.createContext<TooltipScaleContextValue | null>(null);

const resolveUiScale = () => {
    if (typeof window === "undefined") {
        return 1;
    }
    const scaleVar = window
        .getComputedStyle(document.documentElement)
        .getPropertyValue("--ui-scale");
    const scale = Number.parseFloat(scaleVar);
    return Number.isFinite(scale) && scale > 0 ? scale : 1;
};

const computeScaledOffset = (
    trigger: HTMLElement,
    content: HTMLElement,
    scale: number,
) => {
    const triggerRect = trigger.getBoundingClientRect();
    const contentRect = content.getBoundingClientRect();
    const triggerCenterX = triggerRect.left + triggerRect.width / 2;
    const triggerCenterY = triggerRect.top + triggerRect.height / 2;
    const contentCenterX = contentRect.left + contentRect.width / 2;
    const contentCenterY = contentRect.top + contentRect.height / 2;
    const side = content.dataset.side;

    let rawOffsetX = triggerCenterX - contentCenterX;
    let rawOffsetY = triggerCenterY - contentCenterY;

    switch (side) {
        case "top":
            rawOffsetY = triggerRect.top - contentRect.bottom;
            break;
        case "bottom":
            rawOffsetY = triggerRect.bottom - contentRect.top;
            break;
        case "left":
            rawOffsetX = triggerRect.left - contentRect.right;
            break;
        case "right":
            rawOffsetX = triggerRect.right - contentRect.left;
            break;
        default:
            break;
    }

    const divisor = scale > 0 ? scale : 1;
    return {x: rawOffsetX / divisor, y: rawOffsetY / divisor};
};

const composeRefs =
    <T,>(...refs: Array<React.Ref<T> | undefined>) =>
        (node: T) => {
            refs.forEach((ref) => {
                if (!ref) return;
                if (typeof ref === "function") {
                    ref(node);
                } else {
                    (ref as React.MutableRefObject<T | null>).current = node;
                }
            });
        };

function Tooltip({
                     open: openProp,
                     defaultOpen,
                     onOpenChange,
                     ...props
                 }: React.ComponentProps<typeof TooltipPrimitive.Root>) {
    const [openState, setOpenState] = React.useState(defaultOpen ?? false);
    const actualOpen = openProp ?? openState;
    const [triggerNode, setTriggerNode] =
        React.useState<HTMLElement | null>(null);
    const [contentNode, setContentNode] =
        React.useState<HTMLDivElement | null>(null);
    const [offset, setOffset] = React.useState({x: 0, y: 0});

    const handleOpenChange = React.useCallback(
        (next: boolean) => {
            if (openProp === undefined) {
                setOpenState(next);
            }
            onOpenChange?.(next);
        },
        [onOpenChange, openProp],
    );

    React.useLayoutEffect(() => {
        if (!actualOpen || !triggerNode || !contentNode) {
            setOffset({x: 0, y: 0});
            return;
        }
        const scale = resolveUiScale();
        if (scale === 1) {
            setOffset({x: 0, y: 0});
            return;
        }
        const raf = window.requestAnimationFrame(() => {
            setOffset(computeScaledOffset(triggerNode, contentNode, scale));
        });
        return () => window.cancelAnimationFrame(raf);
    }, [actualOpen, contentNode, triggerNode]);

    return (
        <TooltipProvider>
            <TooltipScaleContext.Provider
                value={{
                    registerTrigger: setTriggerNode,
                    registerContent: setContentNode,
                    offset,
                }}
            >
                <TooltipPrimitive.Root
                    data-slot="tooltip"
                    open={actualOpen}
                    onOpenChange={handleOpenChange}
                    {...props}
                />
            </TooltipScaleContext.Provider>
        </TooltipProvider>
    );
}

const TooltipTrigger = React.forwardRef<
    React.ElementRef<typeof TooltipPrimitive.Trigger>,
    React.ComponentProps<typeof TooltipPrimitive.Trigger>
>(({...props}, ref) => {
    const context = React.useContext(TooltipScaleContext);
    const composedRef = composeRefs<React.ElementRef<typeof TooltipPrimitive.Trigger>>(
        ref,
        (node) => {
            context?.registerTrigger(node as HTMLElement | null);
        },
    );
    return (
        <TooltipPrimitive.Trigger
            data-slot="tooltip-trigger"
            ref={composedRef}
            {...props}
        />
    );
});
TooltipTrigger.displayName = "TooltipTrigger";

const TooltipContent = React.forwardRef<
    React.ElementRef<typeof TooltipPrimitive.Content>,
    React.ComponentProps<typeof TooltipPrimitive.Content>
>(({className, sideOffset = 0, children, ...props}, ref) => {
    const context = React.useContext(TooltipScaleContext);
    const composedRef = composeRefs<
        React.ElementRef<typeof TooltipPrimitive.Content>
    >(ref, (node) => {
        context?.registerContent(node as HTMLDivElement | null);
    });
    const offset = context?.offset;
    const translateValue =
        offset && (offset.x !== 0 || offset.y !== 0)
            ? `${offset.x}px ${offset.y}px`
            : undefined;
    return (
        <TooltipPrimitive.Portal>
            <TooltipPrimitive.Content
                ref={composedRef}
                data-slot="tooltip-content"
                sideOffset={sideOffset}
                {...props}
                className={cn(
                    "bg-primary text-primary-foreground animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 w-fit origin-(--radix-tooltip-content-transform-origin) rounded-md px-3 py-1.5 text-xs text-balance",
                    className,
                )}
                style={{
                    ...props.style,
                    ...(translateValue ? {translate: translateValue} : null),
                }}
            >
                {children}
                <TooltipPrimitive.Arrow
                    className="bg-primary fill-primary z-50 size-2.5 translate-y-[calc(-50%_-_2px)] rotate-45 rounded-[2px]"/>
            </TooltipPrimitive.Content>
        </TooltipPrimitive.Portal>
    );
});
TooltipContent.displayName = "TooltipContent";

export {Tooltip, TooltipTrigger, TooltipContent, TooltipProvider};
